import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import type { EmployeeRate, RateType } from '@ip/shared-types';
import { RATE_TYPES } from '@ip/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DatePicker } from '@/components/shared/DatePicker';
import { useEmployeesApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';

const COLUMN_COUNT = 5;

interface RateHistoryEditorProps {
  companyNip: string;
  employeeId: string;
}

export function RateHistoryEditor({ companyNip, employeeId }: RateHistoryEditorProps) {
  const { listRates, addRate, updateRate, removeRate } = useEmployeesApi();
  const [rates, setRates] = useState<EmployeeRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rateType, setRateType] = useState<RateType>('flat_monthly');
  const [amount, setAmount] = useState('');
  const [validFrom, setValidFrom] = useState<Date | null>(new Date());

  const refresh = () => {
    let cancelled = false;
    setLoading(true);
    listRates(companyNip, employeeId)
      .then((r) => { if (!cancelled) setRates(r); })
      .catch((err) => { if (!cancelled) toast.error(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  };

  useEffect(refresh, [companyNip, employeeId, listRates]);

  const openAdd = () => {
    setEditingId(null);
    setRateType('flat_monthly');
    setAmount('');
    setValidFrom(new Date());
    setDialogOpen(true);
  };

  const openEdit = (row: EmployeeRate) => {
    setEditingId(row.id);
    setRateType(row.rateType);
    setAmount(String(row.amount));
    setValidFrom(dayjs(row.validFrom).toDate());
    setDialogOpen(true);
  };

  const handleDelete = async (rateId: string) => {
    try {
      await removeRate(companyNip, employeeId, rateId);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleSave = async () => {
    if (!rateType) {
      toast.error(t('employees.validation.required', { label: t('employees.rateType.label') }));
      return;
    }
    if (!(Number(amount) > 0)) {
      toast.error(t('employees.validation.required', { label: t('employees.rates.amount') }));
      return;
    }
    if (!validFrom) {
      toast.error(t('employees.validation.required', { label: t('employees.rates.validFrom') }));
      return;
    }
    const dto = {
      rateType,
      amount: Number(amount),
      validFrom: dayjs(validFrom).format('YYYY-MM-DD'),
    };
    setBusy(true);
    try {
      if (editingId) {
        await updateRate(companyNip, employeeId, editingId, dto);
      } else {
        await addRate(companyNip, employeeId, dto);
      }
      setDialogOpen(false);
      refresh();
    } catch (err) {
      if ((err as Error).message) toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button onClick={openAdd}>
          <Plus className="size-4" />
          {t('employees.rates.addRate')}
        </Button>
      </div>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground w-[150px] whitespace-nowrap">
                {t('employees.rates.validFrom')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[150px] whitespace-nowrap">
                {t('employees.rates.validTo')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[150px] whitespace-nowrap">
                {t('employees.rateType.label')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[150px] text-right whitespace-nowrap">
                {t('employees.rates.amount')}
              </TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : rates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground h-24 text-center">
                  {t('employees.rates.noRates')}
                </TableCell>
              </TableRow>
            ) : (
              rates.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="py-2">{row.validFrom}</TableCell>
                  <TableCell className="py-2">
                    {row.validTo ?? (
                      <span className="text-emerald-700 dark:text-emerald-400">
                        {t('employees.rates.current')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="py-2">{t(`employees.rateType.${row.rateType}`)}</TableCell>
                  <TableCell className="py-2 text-right tabular-nums">
                    {formatPLN(row.amount)}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        title={t('employees.rates.editRate')}
                        onClick={() => openEdit(row)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive size-8">
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('employees.rates.deleteConfirm')}</AlertDialogTitle>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-white hover:bg-destructive/90"
                              onClick={() => handleDelete(row.id)}
                            >
                              {t('common.delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? t('employees.rates.editRateModal') : t('employees.rates.addRateModal')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rate-type">{t('employees.rateType.label')}</Label>
              <Select value={rateType} onValueChange={(v) => setRateType(v as RateType)}>
                <SelectTrigger id="rate-type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RATE_TYPES.map((rt) => (
                    <SelectItem key={rt} value={rt}>
                      {t(`employees.rateType.${rt}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rate-amount">{t('employees.rates.amount')}</Label>
              <Input
                id="rate-amount"
                type="number"
                min={0.01}
                step={100}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('employees.rates.validFrom')}</Label>
              <DatePicker value={validFrom} onChange={setValidFrom} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={busy} onClick={handleSave}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {editingId ? t('common.save') : t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
